import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Quote, Star, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * USA-based testimonials — each review is ~22–25 words and uses the same
 * narrative voice so the block reads consistently. Names are unique,
 * realistic and representative of clients across US industries.
 */
export const DEFAULT_TESTIMONIALS = [
  { name: "Katherine Ellis", role: "COO, Ridgeway Capital — Boston, MA", quote: "They walked my team through Wikipedia's notability policy in plain English. The final page has been live and unreverted for over two years now.", stars: 5 },
  { name: "Marcus Thompson", role: "Founder, Summit Analytics — San Francisco, CA", quote: "Two previous agencies missed the mark entirely. Prime Wiki Services delivered a policy-compliant page in six weeks and it still reads beautifully today.", stars: 5 },
  { name: "Rebecca Dawson", role: "Author & Columnist — New York, NY", quote: "The editorial team treated my biography like a research project, not a press release. That's why the page holds up under every reviewer check.", stars: 5 },
  { name: "David Pearson", role: "Managing Partner, Hudson Ventures — New York, NY", quote: "Honest feedback, realistic timelines, and zero fluff from day one. Our firm's page has strengthened investor conversations more than we initially expected.", stars: 5 },
  { name: "Jennifer Caldwell", role: "CEO, BlueRidge Health — Denver, CO", quote: "They flagged sourcing gaps we'd been ignoring for years and fixed every one of them before submission. The page has been stable since launch.", stars: 5 },
  { name: "Michael Brennan", role: "Owner, Brennan Design Co. — Brooklyn, NY", quote: "A creative studio rarely fits Wikipedia's rules. Their writers found the right angle, built it around verifiable coverage, and got us published.", stars: 5 },
  { name: "Ashley Morgan", role: "Executive Director, Everlee Foundation — Chicago, IL", quote: "Our nonprofit struggled with repeat deletions before working together. One careful rewrite later, the page has been stable for eighteen months and counting.", stars: 5 },
  { name: "Christopher Hayes", role: "CTO, Waypoint Robotics — Austin, TX", quote: "They understand engineering detail without breaking neutral tone — a rare combination. The draft came back sharper than anything our team could produce in-house.", stars: 5 },
  { name: "Samantha Reed", role: "Principal, Reed + Co. Communications — Los Angeles, CA", quote: "Every milestone was transparent, every revision documented. When my firm's page went live, it felt earned rather than purchased — exactly the difference that matters.", stars: 5 },
];

const initials = (name) =>
  name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

const chunk = (arr, size) => {
  if (size <= 0) return [arr];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Single, brand-consistent accent used for every testimonial card.
const CARD_ACCENT = "from-emerald-500 to-teal-600";

const Card = ({ t }) => (
  <figure className="group relative flex h-full flex-col overflow-hidden rounded-3xl bg-white p-7 shadow-[0_20px_45px_-25px_rgba(10,37,64,0.35)] ring-1 ring-slate-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_30px_60px_-25px_rgba(16,185,129,0.35)] hover:ring-emerald-200">
    <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${CARD_ACCENT}`} />
    <span aria-hidden className="pointer-events-none absolute -top-2 right-4 font-serif text-[120px] leading-none text-slate-100 select-none">"</span>

    <div className="relative flex items-center gap-1 text-amber-500">
      {Array.from({ length: t.stars }).map((_, i) => (
        <Star key={i} className="h-4 w-4 fill-amber-400" />
      ))}
    </div>

    <blockquote className="relative mt-4 flex-1 text-[15px] leading-relaxed text-slate-700">
      <Quote className="mb-2 h-5 w-5 text-emerald-500/70" />
      {t.quote}
    </blockquote>

    <figcaption className="relative mt-6 flex items-center gap-3 border-t border-slate-100 pt-5">
      <div className={`grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br ${CARD_ACCENT} font-serif text-sm font-semibold text-white shadow-md`}>
        {initials(t.name)}
      </div>
      <div className="min-w-0">
        <div className="truncate text-sm font-semibold text-[#0A2540]">{t.name}</div>
        <div className="truncate text-xs text-slate-500">{t.role}</div>
      </div>
    </figcaption>
  </figure>
);

/**
 * TestimonialCarousel — 3 cards per page on md+, 1 per page on mobile.
 * Auto-advances in groups of 3, with manual prev/next, dots and swipe.
 */
export const TestimonialCarousel = ({
  items = DEFAULT_TESTIMONIALS,
  eyebrow = "Testimonials",
  title = "Clients who chose credibility.",
  subtitle = "400+ published pages. Here's what clients say when the dust has settled and the page is still live.",
  autoPlayMs = 5500,
}) => {
  const [perPage, setPerPage] = useState(3);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStartX = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const onChange = () => setPerPage(mq.matches ? 3 : 1);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const pages = useMemo(() => chunk(items, perPage), [items, perPage]);
  const pageCount = pages.length;

  useEffect(() => {
    setIndex((i) => (pageCount ? i % pageCount : 0));
  }, [pageCount]);

  const goto = useCallback((i) => setIndex(((i % pageCount) + pageCount) % pageCount), [pageCount]);
  const next = useCallback(() => goto(index + 1), [goto, index]);
  const prev = useCallback(() => goto(index - 1), [goto, index]);

  useEffect(() => {
    if (paused || pageCount <= 1) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % pageCount), autoPlayMs);
    return () => clearInterval(id);
  }, [paused, pageCount, autoPlayMs]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 40) (dx < 0 ? next : prev)();
    touchStartX.current = null;
  };

  return (
    <section
      className="relative overflow-hidden py-20"
      style={{ background: "linear-gradient(180deg,#0A2540 0%,#0e2c4a 60%,#0A2540 100%)" }}
      data-testid="testimonial-carousel"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(600px 320px at 15% 20%, rgba(16,185,129,0.25) 0%, transparent 60%), radial-gradient(500px 280px at 85% 80%, rgba(59,130,246,0.22) 0%, transparent 60%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-emerald-300 ring-1 ring-emerald-500/30">
            {eyebrow}
          </span>
          <h2 className="font-serif mt-4 text-3xl font-semibold text-white sm:text-4xl">{title}</h2>
          {subtitle ? <p className="mt-3 text-sm text-white/70 sm:text-base">{subtitle}</p> : null}
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-3 text-xs">
          {[
            { v: "4.9 / 5", l: "Average rating" },
            { v: "400+", l: "Verified reviews" },
            { v: "92%", l: "AfC acceptance" },
          ].map((s) => (
            <div
              key={s.l}
              className="inline-flex items-center gap-2 rounded-full bg-white/5 px-4 py-1.5 text-white ring-1 ring-white/10 backdrop-blur"
            >
              <span className="font-serif text-sm text-emerald-300">{s.v}</span>
              <span className="text-white/70">{s.l}</span>
            </div>
          ))}
        </div>

        <div
          className="relative mt-12"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          role="region"
          aria-label="Client testimonials"
          data-testid="testimonial-carousel-track"
        >
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {pages.map((group, pIdx) => (
                <div key={pIdx} className="w-full shrink-0 px-1">
                  <div className={`grid gap-6 ${perPage === 1 ? "grid-cols-1" : "grid-cols-1 md:grid-cols-3"}`}>
                    {group.map((t, i) => (
                      <Card key={`${pIdx}-${i}`} t={t} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={prev}
            aria-label="Previous testimonials"
            className="absolute left-0 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20 md:-left-4 md:inline-flex"
            data-testid="testimonial-prev"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={next}
            aria-label="Next testimonials"
            className="absolute right-0 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/10 p-2.5 text-white ring-1 ring-white/20 backdrop-blur transition-colors hover:bg-white/20 md:-right-4 md:inline-flex"
            data-testid="testimonial-next"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-8 flex items-center justify-center gap-2" data-testid="testimonial-dots">
          {pages.map((_, i) => (
            <button
              type="button"
              key={i}
              onClick={() => goto(i)}
              aria-label={`Go to testimonial page ${i + 1}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === index ? "w-8 bg-emerald-400" : "w-2 bg-white/30 hover:bg-white/50"
              }`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>

        <p className="mt-4 text-center text-xs text-white/50">
          Auto-advances every {Math.round(autoPlayMs / 1000)}s · swipe or hover to pause
        </p>
      </div>
    </section>
  );
};

export default TestimonialCarousel;
